import { useCallback, useEffect, useRef, useState } from 'react';
import { ToastQueue } from '@adobe/react-spectrum';
import {
  useReorderable,
  AFTER_LAST_DROP_TARGET
} from 'app/hooks/useReorderable';
import { reorderReleaseFeatures } from 'app/services/featuresApi';
import { useEventKeyDatePermissions } from 'app/utils/rbacHandler';
import { MOMENT_TYPES } from 'utils/constants';
import type { MonthlyTimelineItem } from '../utils/types';

const DRAG_DROP_REORDER_ENABLED = true;
const REORDER_DEBOUNCE_MS = 600;

const REORDERABLE_MOMENT_TYPES: string[] = [MOMENT_TYPES.MARKETING_MOMENT];

export function getMonthlyFeatureUniqueId(
  feature: MonthlyTimelineItem
): string {
  return (feature.featureId || feature.issueId || feature.id) as string;
}

export function matchesMonthlyFeatureProduct(
  featureProductId: string | undefined,
  draggedProductId: string | null
): boolean {
  return (featureProductId ?? null) === draggedProductId;
}

export function useMonthlyFeatureReorder(
  item: MonthlyTimelineItem,
  sourceFeatures: MonthlyTimelineItem[]
) {
  const { isAdmin } = useEventKeyDatePermissions();
  const reorderDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const draggedProductIdRef = useRef<string | null>(null);
  const [orderedFeatures, setOrderedFeatures] =
    useState<MonthlyTimelineItem[]>(sourceFeatures);
  const [lastReorderedFeatureId, setLastReorderedFeatureId] = useState<
    string | null
  >(null);

  const issueId = item.issueId || '';
  const projectType = item.projectType || item.type || '';

  useEffect(() => {
    if (reorderDebounceRef.current) return;
    setOrderedFeatures(sourceFeatures);
  }, [sourceFeatures]);

  useEffect(() => {
    return () => {
      if (reorderDebounceRef.current) clearTimeout(reorderDebounceRef.current);
    };
  }, []);

  const canEditFeatures = (item.canEdit ?? false) || isAdmin;
  const canReorderFeatures =
    canEditFeatures && !REORDERABLE_MOMENT_TYPES.includes(projectType);

  const handleFeaturesReorder = useCallback(
    (newOrder: MonthlyTimelineItem[], movedItemId?: string) => {
      if (movedItemId) {
        setLastReorderedFeatureId(movedItemId);
        setTimeout(() => setLastReorderedFeatureId(null), 1200);
      }

      const movedFeature = newOrder.find(
        f => getMonthlyFeatureUniqueId(f) === movedItemId
      );
      const affectedProductId =
        (movedFeature as MonthlyTimelineItem & { productId?: string })
          ?.productId ?? null;

      if (reorderDebounceRef.current) clearTimeout(reorderDebounceRef.current);
      reorderDebounceRef.current = setTimeout(async () => {
        reorderDebounceRef.current = null;
        if (!issueId) return;

        const productFeatures = affectedProductId
          ? newOrder.filter(
              (f: MonthlyTimelineItem & { productId?: string }) =>
                f.productId === affectedProductId
            )
          : newOrder;

        const body = {
          features: productFeatures.map((f, i) => ({
            featureId: getMonthlyFeatureUniqueId(f),
            displayOrder: i + 1
          }))
        };

        const res = await reorderReleaseFeatures(issueId, body);
        if (!res.success) {
          ToastQueue.negative('Unable to save feature order.', {
            timeout: 5000
          });
          setOrderedFeatures(sourceFeatures);
        }
      }, REORDER_DEBOUNCE_MS);
    },
    [issueId, sourceFeatures]
  );

  const featureReorder = useReorderable(
    orderedFeatures,
    setOrderedFeatures,
    getMonthlyFeatureUniqueId,
    {
      disabled: !DRAG_DROP_REORDER_ENABLED || !canReorderFeatures,
      rowDataTestIdPrefix: 'monthly-feature-wrapper-',
      dragImageOffsetX: 16,
      dragImageOffsetY: 20,
      onPersist: handleFeaturesReorder
    }
  );

  const draggedProduct = featureReorder.draggedId
    ? (
        orderedFeatures.find(
          f => getMonthlyFeatureUniqueId(f) === featureReorder.draggedId
        ) as MonthlyTimelineItem & { productId?: string }
      )?.productId ?? null
    : null;

  return {
    DRAG_DROP_REORDER_ENABLED,
    AFTER_LAST_DROP_TARGET,
    orderedFeatures,
    featureReorder,
    canEditFeatures,
    canReorderFeatures,
    lastReorderedFeatureId,
    draggedProduct,
    draggedProductIdRef
  };
}
